import { useState } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { StepProps, track, useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, Button, Text } from '@betterment/samba-react';

import StepHeading from '../../AccountCreationFlow/components/StepHeading';
import { LegalAccountOwnershipType, TermsAndAgreementsOption } from '../../graphql/schema';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import { TermsAndAgreementsBlock } from '../../Shared/components-v2/terms-and-agreements/TermsAndAgreementsBlock';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { DirectTradingFlowStateValues } from '../Controller';
import { CreateDirectTradingAccountDocument } from '../graphql/mutation.createDirectTradingAccount.generated';
import { TermsAndAgreementsDocument } from '../graphql/query.termsAndAgreements.generated';

export const getSecuritiesLendingValues = (values: DirectTradingFlowStateValues) => {
  const { isSecurityLendingEnrolled } = values;

  return {
    securitiesLendingAgreement: isSecurityLendingEnrolled ? TermsAndAgreementsOption.SecuritiesLending : null,
    isSecurityLendingEnrolled: isSecurityLendingEnrolled || null,
  };
};

const TermsAndAgreementsStep: React.FC<StepProps<DirectTradingFlowStateValues>> = ({
  goToNextStep,
  goToPreviousStep,
  flowState,
}) => {
  const [creationErrors, setCreationErrors] = useState<string[]>([]);
  const [_, setUnexpectedError] = useState();

  const { securitiesLendingAgreement, isSecurityLendingEnrolled } = getSecuritiesLendingValues(flowState.values);

  const selectedTermsAndAgreements: TermsAndAgreementsOption[] = [
    TermsAndAgreementsOption.DirectTrading,
    ...(securitiesLendingAgreement ? [securitiesLendingAgreement] : []),
  ];

  const { data } = useGraphQLQuery(TermsAndAgreementsDocument, {
    variables: { filter: { termsAndAgreementsOptions: selectedTermsAndAgreements } },
    throwOnError: true,
  });

  const [createDirectTradingAccountMutation] = useGraphQLMutation(CreateDirectTradingAccountDocument);

  const navigate = useAutoNavigate();

  if (!data) return <FullPageLoader />;

  const handleButtonClick = async () => {
    if (isSecurityLendingEnrolled !== null) {
      track('ElementClicked', {
        name: isSecurityLendingEnrolled ? 'SecLendingEnrolled' : 'SecLendingUnEnrolled',
        flow: 'AddDirectTradingAccount',
      });
    }
    const result = await createDirectTradingAccountMutation({
      variables: {
        input: {
          ownershipType: LegalAccountOwnershipType.Individual,
          investingExperienceLevel: flowState.values.investingExperience,
          acceptedAgreements: [TermsAndAgreementsOption.DirectTrading],
          isSecurityLendingEnrolled,
        },
      },
    });
    const account = result.data?.createDirectTradingAccount.result;
    const displayableErrors = result.data?.createDirectTradingAccount.displayableErrors;

    if (displayableErrors && displayableErrors.length > 0) {
      setCreationErrors(displayableErrors.map(e => e.message));
      return;
    }

    if (account != null) {
      goToNextStep({ accountId: account.id });
      return;
    }

    setUnexpectedError(() => {
      throw new Error('Unexpected error on direct trading account opening');
    });
  };

  return (
    <WorkflowLayout
      pageTitle="Terms and Agreement"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
    >
      <Box css={{ display: 'grid', gap: 'space.300' }}>
        <StepHeading>Review and confirm terms.</StepHeading>
        {data.termsAndAgreements.map(termsAndAgreement => (
          <TermsAndAgreementsBlock key={termsAndAgreement.title} termsAndAgreement={termsAndAgreement} />
        ))}
        <Text>By clicking confirm, I acknowledge that I have read and agree to the terms above.</Text>
        {creationErrors.length > 0 &&
          creationErrors.map((errorMessage: string) => (
            <Banner variant="critical">
              <Banner.Content>
                <Text>{errorMessage}</Text>
              </Banner.Content>
            </Banner>
          ))}
        <Box css={{ display: 'flex' }}>
          <Button onClick={handleButtonClick} variant="solidPrimary">
            Confirm
          </Button>
        </Box>
      </Box>
    </WorkflowLayout>
  );
};

export default TermsAndAgreementsStep;
